import React, { useContext, useMemo, useState } from 'react';
import { SalesHistoryContext } from '../SalesHistoryContext';
import { downloadCsv, exportTableToPdf } from '../utils/reportExport';
import { dateKeyFromString } from '../utils/reportDates';

const SalesReportPage = () => {
  const { salesHistory, salesLoading } = useContext(SalesHistoryContext);
  const [filter, setFilter] = useState('');

  const sorted = useMemo(() => {
    const list = Array.isArray(salesHistory) ? [...salesHistory] : [];
    return list.sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
  }, [salesHistory]);

  const rows = useMemo(() => {
    const q = filter.trim();
    if (!q) return sorted;
    return sorted.filter((s) => {
      const dk = dateKeyFromString(s.date);
      if (dk && dk.includes(q)) return true;
      return String(s.date || '').includes(q) || String(s.product?.name || '').toLowerCase().includes(q.toLowerCase());
    });
  }, [sorted, filter]);

  const exportHead = ['Date', 'Product', 'Qty', 'Payment', 'Amount (TZS)', 'Cost (TZS)', 'Profit (TZS)'];
  const exportRows = useMemo(
    () =>
      rows.map((s) => {
        const amount = parseFloat(s.amount) || 0;
        const cost = parseFloat(s.cost);
        const costN = Number.isNaN(cost) ? 0 : cost;
        let profit = parseFloat(s.profit);
        if (Number.isNaN(profit)) {
          profit = !Number.isNaN(parseFloat(s.amount)) && !Number.isNaN(parseFloat(s.cost)) ? amount - costN : 0;
        }
        return [s.date, s.product?.name || '—', s.quantity, s.paymentLabel || s.paymentMethod || '—', amount, costN, profit];
      }),
    [rows]
  );

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, s) => {
        const amount = parseFloat(s.amount) || 0;
        const cost = parseFloat(s.cost);
        const costN = Number.isNaN(cost) ? 0 : cost;
        let profit = parseFloat(s.profit);
        if (Number.isNaN(profit)) {
          profit = !Number.isNaN(parseFloat(s.amount)) && !Number.isNaN(parseFloat(s.cost)) ? amount - costN : 0;
        }
        return {
          amount: acc.amount + amount,
          cost: acc.cost + costN,
          profit: acc.profit + profit,
        };
      },
      { amount: 0, cost: 0, profit: 0 }
    );
  }, [rows]);

  const exportRowsWithTotals = useMemo(() => {
    if (rows.length === 0) return exportRows;
    return [
      ...exportRows,
      ['Totals (filtered)', '', '', '', Math.round(totals.amount), Math.round(totals.cost), Math.round(totals.profit)],
    ];
  }, [exportRows, rows.length, totals]);

  if (salesLoading) {
    return (
      <div className="page-bleed" style={{ background: '#f4f6fa', minHeight: '100vh', paddingTop: 32, paddingBottom: 40 }}>
        <h1 style={{ color: '#2d3748', fontWeight: 700 }}>Sales Report</h1>
        <p style={{ color: '#64748b' }}>Loading sales…</p>
      </div>
    );
  }

  return (
    <div className="page-bleed" style={{ background: '#f4f6fa', minHeight: '100vh', paddingTop: 32, paddingBottom: 40 }}>
      <h1 style={{ color: '#2d3748', fontWeight: 700 }}>Sales Report</h1>
      <p style={{ color: '#64748b', marginBottom: 8 }}>
        POS sales from the server ({rows.length} row{rows.length === 1 ? '' : 's'}). Filter by date (YYYY-MM-DD) or product name.
      </p>
      <p style={{ color: '#0f766e', fontWeight: 600, marginBottom: 8 }}>
        Filtered totals — revenue: <strong>TZS {Math.round(totals.amount).toLocaleString()}</strong>
        {' · '}
        cost: <strong>TZS {Math.round(totals.cost).toLocaleString()}</strong>
        {' · '}
        profit: <strong>TZS {Math.round(totals.profit).toLocaleString()}</strong>
      </p>
      <div style={{ marginBottom: 20 }}>
        <label htmlFor="sales-filter">Filter: </label>
        <input
          id="sales-filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="YYYY-MM-DD or product name…"
          style={{ marginLeft: 8, padding: '8px 12px', minWidth: 260 }}
        />
      </div>
      <table style={{ background: '#fff', borderRadius: 8, width: '100%', padding: 16, borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', padding: 8 }}>Date</th>
            <th style={{ textAlign: 'left', padding: 8 }}>Product</th>
            <th style={{ textAlign: 'right', padding: 8 }}>Qty</th>
            <th style={{ textAlign: 'left', padding: 8 }}>Payment</th>
            <th style={{ textAlign: 'right', padding: 8 }}>Amount (TZS)</th>
            <th style={{ textAlign: 'right', padding: 8 }}>Cost (TZS)</th>
            <th style={{ textAlign: 'right', padding: 8 }}>Profit (TZS)</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={7} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>
                No sales match this filter.
              </td>
            </tr>
          ) : (
            rows.map((s) => {
              const amount = parseFloat(s.amount) || 0;
              const cost = parseFloat(s.cost);
              const costN = Number.isNaN(cost) ? 0 : cost;
              let profit = parseFloat(s.profit);
              if (Number.isNaN(profit)) {
                profit = !Number.isNaN(parseFloat(s.amount)) && !Number.isNaN(parseFloat(s.cost)) ? amount - costN : 0;
              }
              return (
                <tr key={s.id}>
                  <td style={{ padding: 8 }}>{s.date}</td>
                  <td style={{ padding: 8 }}>{s.product?.name || '—'}</td>
                  <td style={{ padding: 8, textAlign: 'right' }}>{s.quantity}</td>
                  <td style={{ padding: 8 }}>{s.paymentLabel || s.paymentMethod}</td>
                  <td style={{ padding: 8, textAlign: 'right' }}>{amount.toLocaleString()}</td>
                  <td style={{ padding: 8, textAlign: 'right' }}>{costN ? costN.toLocaleString() : '—'}</td>
                  <td style={{ padding: 8, textAlign: 'right', fontWeight: 600, color: '#0f766e' }}>
                    {Math.round(profit).toLocaleString()}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
        {rows.length > 0 ? (
          <tfoot>
            <tr style={{ background: '#1e3a5f', color: '#f8fafc' }}>
              <td colSpan={4} style={{ padding: 10, fontWeight: 700 }}>
                Totals <span style={{ fontWeight: 500, opacity: 0.9 }}>(filtered)</span>
              </td>
              <td style={{ padding: 10, textAlign: 'right', fontWeight: 700 }}>{Math.round(totals.amount).toLocaleString()}</td>
              <td style={{ padding: 10, textAlign: 'right', fontWeight: 700 }}>{Math.round(totals.cost).toLocaleString()}</td>
              <td style={{ padding: 10, textAlign: 'right', fontWeight: 700 }}>{Math.round(totals.profit).toLocaleString()}</td>
            </tr>
          </tfoot>
        ) : null}
      </table>
      <div style={{ marginTop: 20 }}>
        <button type="button" onClick={() => downloadCsv('sales-report.csv', [exportHead, ...exportRowsWithTotals])} style={{ marginRight: 10 }}>
          Export to Excel
        </button>
        <button
          type="button"
          onClick={async () => {
            try {
              await exportTableToPdf('Sales Report', exportHead, exportRowsWithTotals);
            } catch (e) {
              // eslint-disable-next-line no-console
              console.error(e);
            }
          }}
        >
          Export to PDF
        </button>
      </div>
    </div>
  );
};

export default SalesReportPage;
